/**
 * useExportExcel.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Hook d'export Excel pour le Top N Dashboard – Nawara BI
 *
 * Structure du classeur généré :
 *  • 1 feuille "Synthèse" : résumé de tous les panels (tableau condensé)
 *  • 1 feuille par panel  : tableau complet avec mise en forme (rang, couleurs, totaux)
 *
 * Dépendances : exceljs  (npm install exceljs)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback } from "react";
import ExcelJS from "exceljs";

// ── Types (miroir de DashboardPage) ──────────────────────────────────────────
export interface RankRow {
  member: string;
  ca: number;
  qte?: number;
}

export interface PanelData {
  id: string;
  title: string;
  icon: string;
  color: string; // hex sans #  ex: "7C3AED"
  rows: RankRow[] | null;
}

export interface ExportExcelOptions {
  filename: string; // sans extension
  dateFrom?: string;
  dateTo?: string;
  topN: number;
  panels: PanelData[];
}

// ── Filtre valeurs indésirables ──────────────────────────────────────────────
const INVALID_DIM_RE = /^\s*(all|unknown|couleur inconnue|inconnue|non renseign[ée]|n\/a|na|null|undefined|#n\/a)\s*$/i;
/** Retourne false si la valeur est vide, "all", "Unknown", "inconnue", etc. */
function isValidDimension(v: string | null | undefined): boolean {
  if (!v || v.trim() === "") return false;
  return !INVALID_DIM_RE.test(v.trim());
}

// ── Utilitaires couleurs ──────────────────────────────────────────────────────

/** Convertit un hex 6-car en { argb: "FF..." } pour ExcelJS */
function hex(color: string): ExcelJS.Color {
  const c = color.replace("#", "").toUpperCase();
  return { argb: `FF${c}` } as ExcelJS.Color;
}

/** Éclaircit une couleur hex de `amount` (0-1) → blanc */
function lighten(color: string, amount: number): ExcelJS.Color {
  const c = color.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const blend = (v: number) =>
    Math.round(v + (255 - v) * amount)
      .toString(16)
      .padStart(2, "0")
      .toUpperCase();
  return { argb: `FF${blend(r)}${blend(g)}${blend(b)}` } as ExcelJS.Color;
}

/** Couleurs médailles */
const MEDAL: Record<number, string> = {
  1: "D97706", // or
  2: "6B7280", // argent
  3: "92400E", // bronze
};

function fmt(n: number): string {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(n);
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Feuille Synthèse ──────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function buildSynthesisSheet(
  wb: ExcelJS.Workbook,
  panels: PanelData[],
  topN: number,
  dateFrom?: string,
  dateTo?: string,
) {
  const ws = wb.addWorksheet("Synthèse", {
    pageSetup: { orientation: "landscape", fitToPage: true },
  });

  // ── Largeurs colonnes ──
  ws.columns = [
    { key: "rank", width: 6 },
    { key: "panel", width: 22 },
    { key: "member", width: 32 },
    { key: "ca", width: 20 },
    { key: "qte", width: 16 },
    { key: "pct", width: 14 },
  ];

  // ── Titre principal ──
  ws.mergeCells("A1:F1");
  const titleCell = ws.getCell("A1");
  titleCell.value = "🏆  Top N Dashboard – AvDash  |  Synthèse";
  titleCell.font = {
    name: "Calibri",
    bold: true,
    size: 16,
    color: hex("7C3AED"),
  };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: hex("F3F0FF"),
  };
  ws.getRow(1).height = 36;

  // ── Sous-titre période ──
  ws.mergeCells("A2:F2");
  const periodeCell = ws.getCell("A2");
  const periodeStr =
    dateFrom && dateTo
      ? `Période : ${dateFrom} → ${dateTo}  |  Top ${topN}`
      : `Toutes dates  |  Top ${topN}`;
  periodeCell.value = periodeStr;
  periodeCell.font = {
    name: "Calibri",
    italic: true,
    size: 11,
    color: hex("9B4DB5"),
  };
  periodeCell.alignment = { horizontal: "center" };
  periodeCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: hex("F9F5FF"),
  };
  ws.getRow(2).height = 20;

  // ── Date génération ──
  ws.mergeCells("A3:F3");
  const genCell = ws.getCell("A3");
  genCell.value = `Généré le : ${new Date().toLocaleString("fr-FR")}`;
  genCell.font = { name: "Calibri", size: 9, color: { argb: "FF64748B" } };
  genCell.alignment = { horizontal: "right" };
  ws.getRow(3).height = 16;

  let row = 5;

  for (const panel of panels) {
    const panelRows = (panel.rows ?? []).filter(r => isValidDimension(r.member));
    if (panelRows.length === 0) continue;

    const total = panelRows.reduce((s, r) => s + r.ca, 0);

    // ── En-tête du panel ──
    ws.mergeCells(`A${row}:F${row}`);
    const panelHeader = ws.getCell(`A${row}`);
    panelHeader.value = `${panel.icon}  ${panel.title}`;
    panelHeader.font = {
      name: "Calibri",
      bold: true,
      size: 13,
      color: { argb: "FFFFFFFF" },
    };
    panelHeader.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: hex(panel.color),
    };
    panelHeader.alignment = {
      horizontal: "left",
      vertical: "middle",
      indent: 1,
    };
    ws.getRow(row).height = 24;
    row++;

    // ── En-têtes colonnes ──
    const headers = [
      "#",
      "Panel",
      "Membre / Libellé",
      "CA (Pttc)",
      "Quantité",
      "% du Total",
    ];
    const hRow = ws.getRow(row);
    headers.forEach((h, i) => {
      const cell = hRow.getCell(i + 1);
      cell.value = h;
      cell.font = {
        name: "Calibri",
        bold: true,
        size: 10,
        color: { argb: "FFFFFFFF" },
      };
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: lighten(panel.color, 0.15),
      };
      cell.alignment = {
        horizontal: i >= 3 ? "right" : "left",
        vertical: "middle",
      };
      cell.border = {
        bottom: { style: "thin", color: hex(panel.color) },
      };
    });
    hRow.height = 18;
    row++;

    // ── Lignes données ──
    panelRows.forEach((r, idx) => {
      const rank = idx + 1;
      const pct = total > 0 ? (r.ca / total) * 100 : 0;
      const dRow = ws.getRow(row);

      // Rang
      const rankCell = dRow.getCell(1);
      rankCell.value = rank;
      rankCell.font = {
        name: "Calibri",
        bold: rank <= 3,
        size: 10,
        color: rank <= 3 ? hex(MEDAL[rank]) : { argb: "FF475569" },
      };
      rankCell.alignment = { horizontal: "center", vertical: "middle" };

      // Panel label (seulement sur la première ligne)
      if (idx === 0) {
        dRow.getCell(2).value = panel.title;
        dRow.getCell(2).font = {
          name: "Calibri",
          size: 10,
          color: hex(panel.color),
        };
      }

      // Membre
      dRow.getCell(3).value = r.member;
      dRow.getCell(3).font = { name: "Calibri", size: 10 };

      // CA
      const caCell = dRow.getCell(4);
      caCell.value = r.ca;
      caCell.numFmt = '#,##0.00 "DT"';
      caCell.font = { name: "Calibri", size: 10, bold: rank === 1 };
      caCell.alignment = { horizontal: "right" };

      // Qte
      const qteCell = dRow.getCell(5);
      qteCell.value = r.qte ?? 0;
      qteCell.numFmt = "#,##0";
      qteCell.alignment = { horizontal: "right" };
      qteCell.font = { name: "Calibri", size: 10 };

      // %
      const pctCell = dRow.getCell(6);
      pctCell.value = pct / 100;
      pctCell.numFmt = "0.00%";
      pctCell.alignment = { horizontal: "right" };
      pctCell.font = { name: "Calibri", size: 10 };

      // Fond alterné
      if (idx % 2 === 1) {
        for (let col = 1; col <= 6; col++) {
          dRow.getCell(col).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: lighten(panel.color, 0.92),
          };
        }
      }

      // Médaille fond
      if (rank <= 3) {
        rankCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: lighten(MEDAL[rank], 0.75),
        };
      }

      dRow.height = 17;
      row++;
    });

    // ── Ligne Total ──
    const totalCa = panelRows.reduce((s, r) => s + r.ca, 0);
    const totalQte = panelRows.reduce((s, r) => s + (r.qte ?? 0), 0);
    const tRow = ws.getRow(row);
    tRow.getCell(3).value = "TOTAL";
    tRow.getCell(3).font = { name: "Calibri", bold: true, size: 10 };
    tRow.getCell(4).value = totalCa;
    tRow.getCell(4).numFmt = '#,##0.00 "DT"';
    tRow.getCell(4).font = { name: "Calibri", bold: true, size: 10 };
    tRow.getCell(4).alignment = { horizontal: "right" };
    tRow.getCell(5).value = totalQte;
    tRow.getCell(5).numFmt = "#,##0";
    tRow.getCell(5).font = { name: "Calibri", bold: true, size: 10 };
    tRow.getCell(5).alignment = { horizontal: "right" };
    tRow.getCell(6).value = 1;
    tRow.getCell(6).numFmt = "0.00%";
    tRow.getCell(6).alignment = { horizontal: "right" };
    tRow.getCell(6).font = { name: "Calibri", bold: true, size: 10 };
    for (let col = 1; col <= 6; col++) {
      tRow.getCell(col).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: lighten(panel.color, 0.8),
      };
      tRow.getCell(col).border = {
        top: { style: "medium", color: hex(panel.color) },
      };
    }
    tRow.height = 18;
    row += 3; // espace entre panels
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Feuille par Panel ─────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

function buildPanelSheet(wb: ExcelJS.Workbook, panel: PanelData, topN: number) {
  const panelRowsFiltered = (panel.rows ?? []).filter(r => isValidDimension(r.member));
  if (panelRowsFiltered.length === 0) return;

  const ws = wb.addWorksheet(`${panel.icon} ${panel.title}`.slice(0, 31), {
    pageSetup: {
      orientation: "portrait",
      fitToPage: true,
      fitToWidth: 1,
    },
    properties: { tabColor: hex(panel.color) },
  });

  // ── Largeurs colonnes ──
  ws.columns = [
    { key: "rank", width: 7 },
    { key: "medal", width: 5 },
    { key: "member", width: 36 },
    { key: "ca", width: 20 },
    { key: "qte", width: 16 },
    { key: "pct", width: 14 },
    { key: "bar", width: 24 },
  ];

  // ── Titre ──
  ws.mergeCells("A1:G1");
  const t1 = ws.getCell("A1");
  t1.value = `${panel.icon}  ${panel.title}`;
  t1.font = {
    name: "Calibri",
    bold: true,
    size: 18,
    color: { argb: "FFFFFFFF" },
  };
  t1.fill = { type: "pattern", pattern: "solid", fgColor: hex(panel.color) };
  t1.alignment = { horizontal: "center", vertical: "middle" };
  ws.getRow(1).height = 40;

  // ── Sous-titre ──
  ws.mergeCells("A2:G2");
  const t2 = ws.getCell("A2");
  t2.value = `Classement des ${topN} meilleurs – CA (Pttc) décroissant`;
  t2.font = {
    name: "Calibri",
    italic: true,
    size: 11,
    color: hex(panel.color),
  };
  t2.alignment = { horizontal: "center" };
  t2.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: lighten(panel.color, 0.9),
  };
  ws.getRow(2).height = 20;

  // ── En-têtes colonnes ──
  const HEADERS = [
    "Rang",
    "🏅",
    "Libellé",
    "CA (DT)",
    "Quantité",
    "% CA",
    "Progression relative",
  ];
  const hRow = ws.getRow(4);
  HEADERS.forEach((h, i) => {
    const cell = hRow.getCell(i + 1);
    cell.value = h;
    cell.font = {
      name: "Calibri",
      bold: true,
      size: 10,
      color: { argb: "FFFFFFFF" },
    };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: hex(panel.color),
    };
    cell.alignment = {
      horizontal: i >= 3 ? "right" : i === 6 ? "left" : "center",
      vertical: "middle",
    };
    cell.border = {
      bottom: { style: "medium", color: { argb: "FFFFFFFF" } },
      right:
        i < 6 ? { style: "thin", color: lighten(panel.color, 0.3) } : undefined,
    };
  });
  hRow.height = 22;

  const total = panelRowsFiltered.reduce((s, r) => s + r.ca, 0);
  const maxCa = panelRowsFiltered[0]?.ca ?? 1;

  // ── Lignes ──
  panelRowsFiltered.forEach((r, idx) => {
    const rank = idx + 1;
    const pct = total > 0 ? (r.ca / total) * 100 : 0;
    const barPct = maxCa > 0 ? (r.ca / maxCa) * 100 : 0;
    // barre textuelle (20 chars max)
    const barLen = Math.round(barPct / 5);
    const bar = "█".repeat(barLen) + "░".repeat(20 - barLen);

    const dRow = ws.getRow(idx + 5);

    // Rang
    const rankCell = dRow.getCell(1);
    rankCell.value = rank;
    rankCell.font = { name: "Calibri", bold: true, size: 11 };
    rankCell.alignment = { horizontal: "center", vertical: "middle" };

    // Médaille emoji
    const medalCell = dRow.getCell(2);
    medalCell.value =
      rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : "";
    medalCell.alignment = { horizontal: "center" };

    // Médaille fond + rang coloré
    if (rank <= 3) {
      const mc = MEDAL[rank];
      rankCell.font = { name: "Calibri", bold: true, size: 12, color: hex(mc) };
      for (let c = 1; c <= 2; c++) {
        dRow.getCell(c).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: lighten(mc, 0.82),
        };
      }
    } else if (idx % 2 === 1) {
      for (let c = 1; c <= 7; c++) {
        dRow.getCell(c).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: lighten(panel.color, 0.93),
        };
      }
    }

    // Membre
    dRow.getCell(3).value = r.member;
    dRow.getCell(3).font = { name: "Calibri", size: 10, bold: rank === 1 };

    // CA
    const caCell = dRow.getCell(4);
    caCell.value = r.ca;
    caCell.numFmt = '#,##0.00 "DT"';
    caCell.font = { name: "Calibri", size: 10, bold: rank === 1 };
    caCell.alignment = { horizontal: "right" };

    // Qte
    const qteCell = dRow.getCell(5);
    qteCell.value = r.qte ?? 0;
    qteCell.numFmt = "#,##0";
    qteCell.alignment = { horizontal: "right" };
    qteCell.font = { name: "Calibri", size: 10 };

    // %
    const pctCell = dRow.getCell(6);
    pctCell.value = pct / 100;
    pctCell.numFmt = "0.00%";
    pctCell.alignment = { horizontal: "right" };
    pctCell.font = { name: "Calibri", size: 10 };

    // Barre de progression texte
    const barCell = dRow.getCell(7);
    barCell.value = bar;
    barCell.font = { name: "Courier New", size: 9, color: hex(panel.color) };
    barCell.alignment = { horizontal: "left" };

    dRow.height = 18;
  });

  // ── Ligne Totaux ──
  const totalRow = panelRowsFiltered.length + 5;
  const totCa = panelRowsFiltered.reduce((s, r) => s + r.ca, 0);
  const totQte = panelRowsFiltered.reduce((s, r) => s + (r.qte ?? 0), 0);

  ws.mergeCells(`A${totalRow}:C${totalRow}`);
  ws.getCell(`A${totalRow}`).value = `TOTAL  (Top ${topN})`;
  ws.getCell(`A${totalRow}`).font = {
    name: "Calibri",
    bold: true,
    size: 11,
    color: hex(panel.color),
  };
  ws.getCell(`A${totalRow}`).alignment = { horizontal: "right" };

  const tcaCell = ws.getCell(`D${totalRow}`);
  tcaCell.value = totCa;
  tcaCell.numFmt = '#,##0.00 "DT"';
  tcaCell.font = { name: "Calibri", bold: true, size: 11 };
  tcaCell.alignment = { horizontal: "right" };

  const tqteCell = ws.getCell(`E${totalRow}`);
  tqteCell.value = totQte;
  tqteCell.numFmt = "#,##0";
  tqteCell.font = { name: "Calibri", bold: true, size: 11 };
  tqteCell.alignment = { horizontal: "right" };

  ws.getCell(`F${totalRow}`).value = 1;
  ws.getCell(`F${totalRow}`).numFmt = "0.00%";
  ws.getCell(`F${totalRow}`).font = { name: "Calibri", bold: true, size: 11 };
  ws.getCell(`F${totalRow}`).alignment = { horizontal: "right" };

  for (let c = 1; c <= 7; c++) {
    ws.getCell(totalRow, c).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: lighten(panel.color, 0.8),
    };
    ws.getCell(totalRow, c).border = {
      top: { style: "medium", color: hex(panel.color) },
      bottom: { style: "double", color: hex(panel.color) },
    };
  }

  // ── Figer la ligne d'en-tête ──
  ws.views = [{ state: "frozen", xSplit: 0, ySplit: 4 }];

  // ── Filtre auto ──
  ws.autoFilter = {
    from: { row: 4, column: 1 },
    to: { row: 4 + panelRowsFiltered.length, column: 6 },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Hook principal ────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────

export function useExportExcel() {
  const exportExcel = useCallback(async (options: ExportExcelOptions) => {
    const { filename, dateFrom, dateTo, topN, panels } = options;

    const wb = new ExcelJS.Workbook();
    wb.creator = "AvDash";
    wb.created = new Date();
    wb.modified = new Date();
    wb.title = `Top N Dashboard – ${filename}`;
    wb.subject = "Classements AvDash";
    wb.keywords = "avdash,bi,topn,export";

    // 1. Feuille synthèse
    buildSynthesisSheet(wb, panels, topN, dateFrom, dateTo);

    // 2. Une feuille par panel
    for (const panel of panels) {
      buildPanelSheet(wb, panel, topN);
    }

    // ── Téléchargement dans le navigateur ──
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, []);

  return { exportExcel };
}